import { chatService } from '@/services/chat.service'
import { useChatStore } from '@/stores/chat-store'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

const PAGE_SIZE = 20

export function useChats() {
  return useQuery({
    queryKey: ['chats'],
    queryFn: () => chatService.getChats(),
  })
}

/** Load or create the user's primary chat id (shared by coach panel and Opportunities). */
export function useEnsureChatId() {
  const chatId = useChatStore((state) => state.chatId)
  const setChatId = useChatStore((state) => state.setChatId)
  const { data: chats, isLoading: chatsLoading } = useChats()
  const createChatMutation = useCreateChat()
  const chatCreationInitiatedRef = useRef(false)

  useEffect(() => {
    if (chatsLoading) return

    if (chats && chats.length > 0) {
      const chat = chats[0]
      if (chat?.id && chat.id !== chatId) {
        setChatId(chat.id)
      }
      return
    }

    if (chatCreationInitiatedRef.current || createChatMutation.isPending) return
    chatCreationInitiatedRef.current = true
    createChatMutation.mutate('My Conversation', {
      onSuccess: (newChat) => {
        setChatId(newChat.id)
      },
      onError: () => {
        chatCreationInitiatedRef.current = false
      },
    })
  }, [chats, chatsLoading, chatId, setChatId, createChatMutation])

  return {
    chatId,
    chatReady: Boolean(chatId),
    chatsLoading: chatsLoading || createChatMutation.isPending,
  }
}

export function useChat(chatId: string) {
  return useQuery({
    queryKey: ['chats', chatId],
    queryFn: () => chatService.getChat(chatId),
    enabled: !!chatId,
  })
}

export function useCreateChat() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (title: string) => chatService.createChat(title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'], exact: true })
    },
  })
}

export function useDeleteChat() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (chatId: string) => chatService.deleteChat(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'], exact: true })
    },
  })
}

export function useSendMessage(chatId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (content: string) => chatService.sendMessage(chatId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats', chatId] })
    },
  })
}

export function useMessages(chatId: string) {
  return useQuery({
    queryKey: ['chats', chatId, 'messages'],
    queryFn: () => chatService.getMessages(chatId),
    enabled: !!chatId,
  })
}

export function useInfiniteMessages(chatId: string) {
  return useInfiniteQuery({
    queryKey: ['chats', chatId, 'messages', 'infinite'],
    queryFn: ({ pageParam = 0 }) => chatService.getMessages(chatId, pageParam, PAGE_SIZE),
    enabled: !!chatId,
    // Always refetch on mount so ChatPage shows the latest messages
    refetchOnMount: 'always',
    // Browser-tab focus must not rebuild paginated history and disturb the
    // reader's scroll anchor. Remounting still refreshes the latest page.
    refetchOnWindowFocus: false,
    // Treat messages as immediately stale so navigation back to chat always refreshes
    staleTime: 0,
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // If we got fewer messages than requested, there are no more
      if (lastPage.length < PAGE_SIZE) {
        return undefined
      }
      // Return the next skip value (total messages loaded so far)
      return allPages.reduce((total, page) => total + page.length, 0)
    },
  })
}
